import {getImagesUrlsFromVariant} from 'features/product/lib/getImagesUrl';
import React, { FC, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { SwiperSlide, Swiper } from 'swiper/react';
import { Swiper as ISwiper } from 'swiper/types';
import { Navigation, Thumbs, Pagination } from 'swiper';

import type { Image as IImage } from 'src/models/IImage';
import { FullImageSwiper } from 'features/modal-swiper';

import { SmallArrowLeft } from 'assets/svg/SmallArrowLeft';
import { SmallArrowRight } from 'assets/svg/SmallArrowRight';
import PlaceholderImageFull from 'assets/images/placeholder.png';
import PlaceholderImage from 'assets/images/placeholder_small.png';

import styles from './styles.module.scss';

const thumbsBreakpoints = {
  1300: { slidesPerView: 7 },
  1550: { slidesPerView: 7 },
};

const renderEmptyPlaceholder = (isThumb?: boolean) => {
  return (
    <div className={styles.slideImage}>
      <Image src={isThumb ? PlaceholderImage : PlaceholderImageFull} alt="" />
    </div>
  );
};

interface Props {
  images?: IImage[];
}

const ProductImages: FC<Props> = ({ images }) => {
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [mainSwiper, setMainSwiper] = useState<ISwiper | null>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const router = useRouter();

  const mainImages = getImagesUrlsFromVariant(images, 'large_webp', 'large');
  const thumbsImages = getImagesUrlsFromVariant(images, 'small_webp', 'small');
  const originalImages = getImagesUrlsFromVariant(images, 'original', 'large');

  const handleClose = (index: number) => {
    setIsFullScreen(false);
    if (mainSwiper) mainSwiper.slideToLoop(Number(index));
  };

  const handleClickSlide = (index) => {
    return () => {
      setIsFullScreen(true);
      setCurrentSlide(index);
    };
  };

  const renderMainImages = () => {
    return mainImages.map((img, index) => (
      <SwiperSlide key={`main-slide-${img.id}`} onClick={handleClickSlide(index)}>
        <div className={styles.slideImage}>
          <Image src={img.url} width={780} height={550} alt="main image" />
        </div>
      </SwiperSlide>
    ));
  };

  const renderThumbImages = () => {
    return thumbsImages.map((img) => (
      <SwiperSlide key={`thumb-slide-${img.id}`} className={styles.thumbsSlide}>
        <div className={`thumb-slide-image ${styles.thumbsSlideImage}`}>
          <Image
            src={img.url}
            width={img.meta.width}
            height={img.meta.height}
            alt="изображение продукта"
            className="goodDetailSlideImg"
          />
        </div>
      </SwiperSlide>
    ));
  };

  useEffect(() => {
    if (isFullScreen) {
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = 'auto';
    }
  }, [isFullScreen]);

  const loop = mainImages?.length > 1;

  useEffect(() => {
    if (mainSwiper && !mainSwiper.destroyed) {
      mainSwiper.update();
    }
  }, [mainSwiper, router.asPath]);

  const moreOneImage = mainImages.length > 1;

  const [isActive, setActive] = useState(false);

  useEffect(() => {
    setActive(isFullScreen);
  }, [isFullScreen]);

  return (
    <>
      {isFullScreen && !!images?.length && (
        <FullImageSwiper
          onClose={handleClose}
          images={originalImages}
          isOpen={isActive}
          thumbsImages={thumbsImages}
          currentSlide={currentSlide}
        />
      )}

      <div className="goodDetailsSwiper">
        <div className={styles.images}>
          {mainImages.length > 0 ? (
            <div className={styles.mainSwiperWrapper}>
              <Swiper
                centerInsufficientSlides
                className={styles.mainSwiper}
                modules={[Navigation, Thumbs, Pagination]}
                speed={200}
                loop={loop}
                loopedSlides={mainImages.length}
                allowTouchMove
                thumbs={{ swiper: thumbsSwiper && !thumbsSwiper?.destroyed ? thumbsSwiper : null }}
                onUpdate={(swiper) => (loop ? swiper.slideToLoop(0, 0, false) : swiper.slideTo(0, 0, false))}
                pagination={{
                  el: '.product-images-pagination',
                  type: 'bullets',
                }}
                onSwiper={setMainSwiper}
              >
                {Boolean(mainImages.length) && renderMainImages()}
              </Swiper>
              <button
                onClick={() => mainSwiper?.slidePrev()}
                className={`${styles.thumbsNavButton} product-swiper__prev`}
                disabled={!loop || !mainSwiper}
              >
                <SmallArrowLeft />
              </button>
              <button
                onClick={() => mainSwiper?.slideNext()}
                className={`${styles.thumbsNavButton} ${styles.thumbsNavButtonNext} product-swiper__next`}
                disabled={!loop || !mainSwiper}
              >
                <SmallArrowRight />
              </button>
            </div>
          ) : (
            renderEmptyPlaceholder()
          )}
          {
            <>
              <div className={`${styles.pagination} product-images-pagination`} />
              <div className={`good-thumb-swiper ${styles.thumbsSwiper} ${moreOneImage ? styles.thumbsActive : ''}`}>
                <button
                  onClick={() => mainSwiper?.slidePrev()}
                  className={`${styles.thumbBottomButton} product-swiper__prev`}
                  disabled={!loop || !mainSwiper}
                >
                  <SmallArrowLeft />
                </button>
                <button
                  onClick={() => mainSwiper?.slideNext()}
                  className={`${styles.thumbBottomButtonNext} ${styles.thumbsNavButton} ${styles.thumbsNavButtonNext} product-swiper__next`}
                  disabled={!loop || !mainSwiper}
                >
                  <SmallArrowRight />
                </button>
                <Swiper
                  centerInsufficientSlides
                  className={styles.swiper}
                  modules={[Thumbs]}
                  watchSlidesProgress
                  slidesPerView={7}
                  spaceBetween={6}
                  speed={200}
                  slideToClickedSlide
                  breakpoints={thumbsBreakpoints}
                  onSwiper={setThumbsSwiper}
                >
                  {Boolean(thumbsImages.length) && renderThumbImages()}
                </Swiper>
              </div>
            </>
          }
        </div>
      </div>
    </>
  );
};

const ProductImagesMemo = React.memo(ProductImages);

export { ProductImagesMemo };

export default ProductImages;
